import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Snackbar, Alert, AlertTitle, LinearProgress,
    Box, Typography, IconButton, Collapse
} from '@mui/material';
import {
    CloudDownload as DownloadIcon,
    CheckCircle as SuccessIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon
} from '@mui/icons-material';

/**
 * Global sync progress notification.
 * Listens to:
 *  - 'syncAllProgress'         — overall progress (which entity of N is syncing)
 *  - 'syncEntityProgress'      — individual entity start/done
 *  - 'equipmentSyncProgress'   — paginated progress for large tables (records loaded)
 */
// Если между событиями синка прошло столько времени — считаем что процесс
// оборвался (сеть/ошибка/закрытая вкладка) и убираем тост. Две минуты: живой
// синк столько молчать не должен даже на медленном 4G.
const WATCHDOG_MS = 120000;

export default function SyncProgressNotification() {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);

    // Overall sync state
    const [syncState, setSyncState] = useState({
        phase: null,       // 'start' | 'progress' | 'done' | 'error'
        total: 0,
        current: 0,
        currentKey: null,
    });

    // Paginated detail (for equipment/locations/PO)
    const [paginatedDetail, setPaginatedDetail] = useState(null);

    // Таймеры автозакрытия. hideTimer — плановое закрытие после done/error,
    // watchdog — страховка: если синк оборвался и события больше не приходят,
    // тост всё равно уйдёт, а не будет висеть поверх экрана (баг «Syncing Jha
    // Document Triggers» который нельзя было закрыть).
    const hideTimerRef = useRef(null);
    const watchdogRef = useRef(null);

    // Зеркало syncState: обработчикам событий нужно читать актуальную фазу,
    // не переоткрывая подписки на каждый рендер.
    const syncStateRef = useRef({ phase: null, total: 0, current: 0, currentKey: null });

    const applySyncState = useCallback((next) => {
        syncStateRef.current = next;
        setSyncState(next);
    }, []);

    const scheduleHide = useCallback((ms) => {
        clearTimeout(watchdogRef.current);
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setOpen(false), ms);
    }, []);

    // Перевод стрелки watchdog'а: пока события синка идут, тост живёт
    const bumpWatchdog = useCallback(() => {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = setTimeout(() => setOpen(false), WATCHDOG_MS);
    }, []);

    // Начало нового цикла: заодно снимаем плановое закрытие от прошлого
    const armWatchdog = useCallback(() => {
        clearTimeout(hideTimerRef.current);
        bumpWatchdog();
    }, [bumpWatchdog]);

    // Handle overall sync progress
    const handleSyncAllProgress = useCallback((e) => {
        const { phase, total, current, currentKey } = e.detail;

        if (phase === 'start') {
            setOpen(true);
            setExpanded(false);
            setPaginatedDetail(null);
            applySyncState({ phase, total, current: 0, currentKey: null });
            armWatchdog();
        } else if (phase === 'progress') {
            // «start» мог пройти до монтирования (вход: синк стартует сразу
            // после перехода в приложение) — открываемся и на «progress».
            setOpen(true);
            applySyncState({ phase, total, current, currentKey });
            setPaginatedDetail(null); // Reset paginated detail when switching entity
            armWatchdog();
        } else if (phase === 'done') {
            applySyncState({ phase, total, current: total, currentKey: null });
            setPaginatedDetail(null);
            // Auto-hide after 4 seconds
            scheduleHide(4000);
        } else if (phase === 'error') {
            applySyncState({ ...syncStateRef.current, phase: 'error' });
            scheduleHide(6000);
        }
    }, [applySyncState, armWatchdog, scheduleHide]);

    // Handle individual entity progress (for single sync, not syncAll)
    const handleEntityProgress = useCallback((e) => {
        const { key, phase } = e.detail;

        // Пока идёт syncAll — он сам рисует общий прогресс, отдельные
        // сущности его не перебивают. Но событие всё равно означает «синк жив».
        const syncAllRunning = syncStateRef.current.total > 1
            && (syncStateRef.current.phase === 'start' || syncStateRef.current.phase === 'progress');
        if (syncAllRunning) {
            bumpWatchdog();
            return;
        }

        if (phase === 'start') {
            setOpen(true);
            applySyncState({ phase: 'progress', total: 1, current: 0, currentKey: key });
            armWatchdog();
        } else if (phase === 'done' || phase === 'error') {
            // 'error' обязателен: без него упавший синк оставлял тост висеть.
            setPaginatedDetail(null);
            applySyncState({ phase, total: 1, current: 1, currentKey: key });
            scheduleHide(phase === 'done' ? 3000 : 6000);
        }
    }, [applySyncState, armWatchdog, bumpWatchdog, scheduleHide]);

    // Handle paginated sync detail (equipment, equipLocations, purchaseOrders)
    const handlePaginatedProgress = useCallback((e) => {
        const { label, phase, total, loaded } = e.detail;

        // Большие таблицы качаются страницами дольше окна watchdog'а —
        // каждая страница подтверждает, что синк идёт.
        bumpWatchdog();

        if (phase === 'start' || phase === 'progress') {
            setPaginatedDetail({ label, total, loaded });
        } else if (phase === 'done') {
            setPaginatedDetail({ label, total: loaded, loaded, done: true });
        }
    }, [bumpWatchdog]);

    useEffect(() => {
        window.addEventListener('syncAllProgress', handleSyncAllProgress);
        window.addEventListener('syncEntityProgress', handleEntityProgress);
        window.addEventListener('equipmentSyncProgress', handlePaginatedProgress);
        return () => {
            window.removeEventListener('syncAllProgress', handleSyncAllProgress);
            window.removeEventListener('syncEntityProgress', handleEntityProgress);
            window.removeEventListener('equipmentSyncProgress', handlePaginatedProgress);
            clearTimeout(hideTimerRef.current);
            clearTimeout(watchdogRef.current);
        };
    }, [handleSyncAllProgress, handleEntityProgress, handlePaginatedProgress]);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway' && syncState.phase === 'progress') return;
        clearTimeout(hideTimerRef.current);
        clearTimeout(watchdogRef.current);
        setOpen(false);
    };

    const isDone = syncState.phase === 'done';
    const isError = syncState.phase === 'error';
    const overallPercent = syncState.total > 0
        ? Math.round((syncState.current / syncState.total) * 100)
        : 0;

    const severity = isDone ? 'success' : isError ? 'error' : 'info';

    const formatKey = (key) => {
        if (!key) return '';
        return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={null}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Alert
                onClose={handleClose}
                severity={severity}
                icon={isDone ? <SuccessIcon /> : <DownloadIcon />}
                sx={{ minWidth: 340, maxWidth: 420, boxShadow: 3 }}
                action={
                    !isDone && !isError && syncState.total > 1 ? (
                        <IconButton size="small" onClick={() => setExpanded(p => !p)}>
                            {expanded ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                    ) : null
                }
            >
                <AlertTitle sx={{ fontWeight: 600 }}>
                    {isDone
                        ? 'Sync Complete'
                        : isError
                            ? 'Sync Error'
                            : syncState.total > 1
                                ? `Syncing ${syncState.current + 1} / ${syncState.total}`
                                : `Syncing ${formatKey(syncState.currentKey)}`
                    }
                </AlertTitle>

                {/* Current entity name */}
                {!isDone && !isError && syncState.currentKey && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {formatKey(syncState.currentKey)}...
                    </Typography>
                )}

                {isDone && (
                    <Typography variant="body2">
                        {syncState.total > 1
                            ? `All ${syncState.total} tables synced successfully`
                            : `${formatKey(syncState.currentKey)} synced`}
                    </Typography>
                )}

                {isError && (
                    <Typography variant="body2">
                        {syncState.currentKey
                            ? `${formatKey(syncState.currentKey)} could not be synced. It will retry on the next sync.`
                            : 'Sync could not be completed. It will retry on the next sync.'}
                    </Typography>
                )}

                {/* Overall progress bar */}
                {!isDone && !isError && syncState.total > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress
                                variant="determinate"
                                value={overallPercent}
                                sx={{ height: 6, borderRadius: 1 }}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right' }}>
                            {overallPercent}%
                        </Typography>
                    </Box>
                )}

                {/* Paginated detail (for large tables) */}
                <Collapse in={expanded || (paginatedDetail && !paginatedDetail.done)}>
                    {paginatedDetail && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {paginatedDetail.label}: {paginatedDetail.loaded.toLocaleString()} / {paginatedDetail.total.toLocaleString()} records
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={paginatedDetail.total > 0
                                    ? Math.round((paginatedDetail.loaded / paginatedDetail.total) * 100)
                                    : 0
                                }
                                color="secondary"
                                sx={{ height: 4, borderRadius: 1, mt: 0.5 }}
                            />
                        </Box>
                    )}
                </Collapse>
            </Alert>
        </Snackbar>
    );
}
